import React, { Dispatch, SetStateAction, useContext } from 'react';
import { Components } from './styled';
import { Section, Tab, TabType } from '../../../../api/appConfiguration/types';
import SectionCard from '../SectionCard';
import useModalState from '../../../../hooks/useModalState';
import CreateOrEditSectionModal from '../../../../components/modals/CreateOrEditSectionModal';
import { setAppConfiguration } from '../../../../api/appConfiguration';
import { ToastContext } from '../../../../providers/ToastProvider/context';
import { getProjectErrorForCode } from '../../../../utils/errors';
import { ProjectErrors } from '../../../../utils/errors/types';
import { SessionContext } from '../../../../providers/SessionProvider/context';

const TabComponent = ({
  title,
  sections,
  appConfiguration,
  tabType,
  onMoveUpSection,
  onMoveDownSection,
  onDeleteSection,
  setTabs,
}: {
  title: string;
  sections: Section[];
  appConfiguration: Tab[];
  tabType: TabType;
  onEditSection: (itemKey: string, section: Section) => void;
  onMoveUpSection: (itemKey: string) => void;
  onMoveDownSection: (itemKey: string) => void;
  onDeleteSection: (itemKey: string) => void;
  refreshCallback: () => Promise<void>;
  tabs: Tab[];
  setTabs: Dispatch<SetStateAction<Tab[]>>;
}) => {
  const { showSuccessToast, showErrorToast } = useContext(ToastContext);
  const { currentProfile } = useContext(SessionContext);

  const createSectionModalProps = useModalState<{ section?: Section; tab?: TabType; appConfiguration?: Tab[] }>();

  const _onCreateSectionModalOpen = () => {
    createSectionModalProps.open({ section: undefined, tab: tabType, appConfiguration });
  };

  const onEditSectionModalOpen = (itemKey: string, section: Section) => {
    createSectionModalProps.open({ section, tab: tabType, appConfiguration });
  };

  const _onCreateOrEditSectionModalClose = () => {
    createSectionModalProps.onModalClose();
  };

  const onCreateOrUpdateSection = async (section: Section) => {
    if (currentProfile) {
      setTabs((prevTabs) => {
        const newTabs = prevTabs.map((tab) => {
          if (tab.tab !== tabType) {
            return tab;
          }

          const newSections = [...tab.sections];
          const sectionIndex = newSections.findIndex((s) => s.key === section.key);
          if (sectionIndex === -1) {
            newSections.push(section);
          } else {
            newSections[sectionIndex] = section;
          }

          return {
            ...tab,
            sections: newSections,
          };
        });

        setAppConfiguration(newTabs).then((res) => {
          if (!res) {
            showSuccessToast('Successfully saved section.');
          } else {
            showErrorToast(getProjectErrorForCode(ProjectErrors.failedToSaveSection));
          }
        });

        return newTabs;
      });
    } else {
      showErrorToast(getProjectErrorForCode(ProjectErrors.superUserPermissions));
    }
  };

  return (
    <Components.Container>
      <Components.TabTitle>{title}</Components.TabTitle>
      <Components.Tab>
        {sections.map((section) => (
          <SectionCard
            key={section.key}
            section={section}
            onEdit={onEditSectionModalOpen}
            onMoveUp={onMoveUpSection}
            onMoveDown={onMoveDownSection}
            onDelete={onDeleteSection}
          />
        ))}
        <Components.Button onClick={_onCreateSectionModalOpen}>
          <Components.ButtonLabel>Create Section</Components.ButtonLabel>
        </Components.Button>
      </Components.Tab>
      <CreateOrEditSectionModal
        {...createSectionModalProps}
        onModalClose={_onCreateOrEditSectionModalClose}
        onCreateOrUpdateSection={onCreateOrUpdateSection}
      />
    </Components.Container>
  );
};

export default TabComponent;
